<template>
    <div class="card" :class="dependencyError ? 'card-error' : 'card-ok'">
        <div class="card-header">
            <div class="card-header-title">
                <!--<div v-if="dependencyError" class="alert alert-danger"><strong>Error:</strong> {{ dependencyError }}</div>-->
                <div v-if="error" class="alert alert-warning"><strong>Warning:</strong> {{ error }}</div>
                <span>{{ mainObservationTitle }}</span>
            </div>

            <div class="card-header-use-dependency-container">
                <button-radio-container :options="{ 0: 'No dependencies', 1: 'Use dependency' }" cast="integer" v-model="hasDependency" />
            </div>
        </div>
        <div class="card-body" v-if="hasDependency">
            <div class="input-group mb-3">
                <label class="col-sm-3 col-form-label">Indicator</label>

                <select class="form-control" v-model="internalIndicatorId">
                    <option value="">Select indicator</option>
                    <option
                            v-for="displayIndicatorId in displayIndicatorIds"
                            :key="displayIndicatorId"
                            :value="displayIndicatorId"
                            v-text="displayIndicators[displayIndicatorId]"
                    />
                </select>
            </div>
            <div class="input-group mb-3" v-if="internalIndicatorId">
                <label class="col-sm-3 col-form-label">Observation group</label>

                <select class="form-control" v-model="internalObservationGroupId">
                    <option value="">Select observation group</option>
                    <option
                            v-if="internalIndicatorId"
                            v-for="displayObservationGroupId in displayObservationGroupIds"
                            :key="displayObservationGroupId"
                            :value="displayObservationGroupId"
                            v-text="displayObservationGroups[displayObservationGroupId]"
                    />
                </select>
            </div>
            <div class="input-group mb-3" v-if="internalObservationGroupId">
                <label class="col-sm-3 col-form-label">Observation</label>

                <select class="form-control" v-model="internalObservationId">
                    <option value="">Select observation</option>
                    <option
                            v-if="internalObservationGroupId"
                            v-for="displayObservationId in displayObservationIds"
                            :key="displayObservationId"
                            :value="displayObservationId"
                            v-text="displayObservations[displayObservationId]"
                    />
                </select>
            </div>
        </div>
    </div>
</template>

<script>
    import { $eBus } from '@/utils/eventBus';
    import { mapGetters } from 'vuex';
    import ButtonRadioContainer from "../../common/ButtonRadioContainer";
    import { getIndicatorTitle, getObservationTitle, getObservationGroupTitle } from "@/utils/admin/titleGenerator";
    import { replacePersonVariables } from '@/utils/replacements/personVariables';
    import { validateObservationDependency } from '@/utils/admin/indicators/ExceptionValidator';

    export default {
        name: 'observation-dependency-row',
        components: {ButtonRadioContainer},
        props: {
            observation: {
                type: Object,
            },
            value: {
                type: Object,
                default() {
                    return null;
                }
            },
            allIndicators: {
                type: Array
            },
        },
        beforeMount() {
            $eBus.$on('forceValidate', this.validate);
        },
        beforeDestroy() {
            $eBus.$off('forceValidate', this.validate);
        },
        data() {
            return {
                dependencyError: null,

                watchValue: true,
                doUpdateValues: true,
                hasDependency: 0,

                internalIndicatorId: '',
                activeIndicator: null,
                indicators: {},
                displayIndicatorIds: [],
                displayIndicators: {},

                internalObservationGroupId: '',
                activeObservationGroup: null,
                observationGroups: {},
                displayObservationGroupIds: [],
                displayObservationGroups: {},

                internalObservationId: '',
                activeObservation: null,
                observations: {},
                displayObservationIds: [],
                displayObservations: {},
            }
        },
        mounted() {
            for (let indicator of this.scenarioIndicators) {
                this.displayIndicatorIds.push(indicator.id);
                this.displayIndicators[indicator.id] = getIndicatorTitle(indicator);
                this.indicators[indicator.id] = indicator;
            }

            if (!this.value) {
                this.updateValues();
            } else if (this.value) {
                this.updateFromValue(this.value);
            }
        },
        computed: {
            ...mapGetters({
                'activeScenario': 'getActiveScenario',
                'activeScenarioData': 'getActiveScenarioData',
                'scenarioIndicators': 'getScenarioIndicators',
                'activeChild': 'getScenarioActiveChild',
                'activeAdult': 'getScenarioActiveAdult',
            }),
            mainObservationTitle() {
                let observationTitle = getObservationTitle(this.observation);

                return replacePersonVariables(observationTitle, this.activeChild, this.activeAdult);
            },
            error() {
                if (this.activeScenario.session.line === 'first' && this.observation.interactionPoint === 'luggage') {
                    return 'Observations assigned to luggage will not show up in FLC scenarios';
                }

                return null;
            },
        },
        methods: {
            validate(values = null) {
                if (!this.hasDependency) {
                    this.dependencyError = null;

                    return true; // It's definitely OK, because we don't have a dependency
                }

                let castValues;

                if (!values) {
                    castValues = this.getValues();
                } else {
                    castValues = {
                        indicatorId: parseInt(values.indicatorId, 10) || null,
                        observationGroupId: parseInt(values.observationGroupId, 10) || null,
                        observationId: parseInt(values.observationId, 10) || null,
                    };
                }

                try {
                    validateObservationDependency(castValues, this.allIndicators, this.observation.id);

                    this.dependencyError = null;

                    return true;
                } catch (error) {
                    this.dependencyError = error.message || error.toString();

                    return false;
                }
            },
            getValues() {
                return {
                    indicatorId: parseInt(this.internalIndicatorId, 10) || null,
                    observationGroupId: parseInt(this.internalObservationGroupId, 10) || null,
                    observationId: parseInt(this.internalObservationId, 10) || null,
                };
            },
            updateValues() {
                this.watchValue = false;

                let values = this.getValues();

                this.validate(values);

                this.$emit('input', values);

                this.$nextTick(() => {
                    this.watchValue = true;
                })
            },
            updateFromValue(value) {
                if (!this.watchValue) {
                    return;
                }

                this.doUpdateValues = false;

                this.internalIndicatorId = value.indicatorId ? value.indicatorId.toString() : '';
                this.internalObservationGroupId = value.observationGroupId ? value.observationGroupId.toString() : '';
                this.internalObservationId = value.observationId ? value.observationId.toString() : '';

                this.hasDependency = (this.internalIndicatorId || this.internalObservationGroupId || this.internalObservationId) ? 1 : 0;

                this.validate();

                this.$nextTick(() => {
                    this.doUpdateValues = true;
                });
            }
        },
        watch: {
            value(value) {
                this.updateFromValue(value);
            },
            hasDependency(hasDependency) {
                if (!hasDependency) {
                    this.doUpdateValues = false;

                    this.internalIndicatorId = '';
                    this.internalObservationGroupId = '';
                    this.internalObservationId = '';

                    this.$nextTick(() => {
                        this.updateValues();
                    })
                }

                this.$nextTick(() => {
                    $eBus.$emit('forceValidate');
                });
            },
            internalIndicatorId(indicatorId) {
                let displayObservationGroupIds = [];
                let displayObservationGroups = {};

                indicatorId = parseInt(indicatorId, 10) || null;

                for (let indicator of this.scenarioIndicators) {
                    if (indicatorId === indicator.id) {
                        for (let observationGroupId in indicator.observationGroups) {
                            let observationGroup = indicator.observationGroups[observationGroupId];

                            this.activeIndicator = indicator;

                            displayObservationGroupIds.push(observationGroupId);
                            displayObservationGroups[observationGroupId] = getObservationGroupTitle(observationGroup);
                        }
                    }
                }

                this.displayObservationGroupIds = displayObservationGroupIds;
                this.displayObservationGroups = displayObservationGroups;

                if (this.doUpdateValues) {
                    this.internalObservationGroupId = ''
                    this.internalObservationId = '';

                    this.updateValues();
                }
            },

            internalObservationGroupId(observationGroupId) {
                let displayObservationIds = [];
                let displayObservations = {};

                observationGroupId = parseInt(observationGroupId, 10) || null;

                if (observationGroupId && this.activeIndicator) {
                    this.activeObservationGroup = this.activeIndicator.observationGroups[observationGroupId];

                    for (let observationId in this.activeObservationGroup.observations) {
                        let observation = this.activeObservationGroup.observations[observationId];

                        displayObservationIds.push(observationId);
                        displayObservations[observationId] = getObservationTitle(observation);
                    }
                } else {
                    this.activeObservationGroup = null;
                }

                this.displayObservationIds = displayObservationIds;
                this.displayObservations = displayObservations;

                if (this.doUpdateValues) {
                    this.internalObservationId = '';

                    this.updateValues();
                }
            },

            internalObservationId(observationId) {
                observationId = parseInt(observationId, 10) || null;

                this.updateValues();

                this.$nextTick(() => {
                    $eBus.$emit('forceValidate');
                });
            },
        }
    }
</script>

<style lang="scss" scoped>
    .observation-dependency-row {
        padding-top: 15px;
        background: rgba(0, 0, 0, 0.03);
        border: 1px solid rgba(0, 0, 0, 0.125);
    }

    .card-header-title {
        float: left;
        line-height: 38px;
    }

    .card-header-use-dependency-container {
        float: right;
    }

    .card {
        &.card-ok {

        }

        &.card-error {
            background: #ffbcbcbc;
        }
    }
</style>
